import React, { useState } from 'react'
import { Form, Formik } from 'formik'
import Modal from '../../../Modal/Modal'
import Input from '../../../FormComponents/Input'
import Textarea from '../../../FormComponents/Textarea'
import CurrencySelect from '../../../FormComponents/CurrencySelect'
import { toastError, toastSuccess } from '../../../FormComponents/Toasts'
import axiosService from '../../../../utils/AxiosService'
import { formatProvider, providerLogo } from '../../../../utils/Helper'
import { ManualReachbirdCollaborationSchema } from '../../../../schemas/ManualReachbirdCollaborationSchema'

const CHANNELS = ['instagram', 'tiktok', 'youtube', 'linkedin', 'pinterest']

const dateInputClass = 'rb-date-input h-11 w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter px-3 bg-white dark:bg-rb-dark-mode text-rb-black dark:text-rb-dark-mode-text-grey [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-0 focus:border-rb-green focus:border-2'

const labelClass = 'block mb-1 text-sm font-semibold text-rb-black dark:text-white'

const initialValuesFor = (collaboration) => ({
  campaign_name: collaboration?.campaign_name || '',
  brand: collaboration?.brand || '',
  start_date: collaboration?.start_date || '',
  end_date: collaboration?.end_date || '',
  providers: collaboration?.providers || [],
  payout_amount: collaboration?.payout_amount ?? '',
  currency: collaboration?.currency || 'EUR',
  notes: collaboration?.notes || ''
})

export default function ReachbirdCollaborationModal({ influencerId, collaboration, isOpen, onClose, onSaved }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const isEdit = !!collaboration?.id
  const baseUrl = `/api/project_manager/influencers/${influencerId}/manual_reachbird_collaborations`

  const close = () => {
    setConfirmingDelete(false)
    onClose()
  }

  const save = (values, { setSubmitting }) => {
    const payload = {
      manual_reachbird_collaboration: {
        ...values,
        campaign_name: values.campaign_name.trim(),
        brand: values.brand.trim(),
        payout_amount: String(values.payout_amount ?? '').trim() === '' ? null : String(values.payout_amount).replace(',', '.').trim(),
        start_date: values.start_date || null,
        end_date: values.end_date || null
      }
    }
    const request = isEdit ? axiosService.patch(`${baseUrl}/${collaboration.id}`, payload) : axiosService.post(baseUrl, payload)

    return request
      .then(() => {
        toastSuccess(isEdit ? 'Collaboration updated' : 'Collaboration added')
        onSaved()
        close()
      })
      .catch(({ data: { error } }) => {
        toastError(error)
        setSubmitting(false)
      })
  }

  const destroy = () => {
    setDeleting(true)
    axiosService.delete(`${baseUrl}/${collaboration.id}`)
      .then(() => {
        toastSuccess('Collaboration deleted')
        onSaved()
        close()
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setDeleting(false))
  }

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <Modal.Header>
        <span className="font-bold text-lg dark:text-white">{isEdit ? 'Edit past collaboration' : 'Add past collaboration'}</span>
      </Modal.Header>
      <Formik
        validationSchema={ManualReachbirdCollaborationSchema}
        initialValues={initialValuesFor(collaboration)}
        enableReinitialize
        onSubmit={save}
      >
        {({ handleBlur, handleChange, setFieldValue, values, errors, touched, isSubmitting }) => (
          <Form>
            <Modal.Body>
              <div className="w-[560px] flex flex-col gap-4">
                <p className="text-xs text-rb-lighter-grey dark:text-gray-400">For Reachbird campaigns that ran before the platform. Campaigns run in the tool are listed automatically once they have ended.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className={labelClass}>Campaign name</label>
                    <Input autoFocus topMargin="mt-0" inputPaddingClass="px-3" name="campaign_name" placeholder="e.g. Summer Launch 2023" value={values.campaign_name} onChange={handleChange} onBlur={handleBlur} error={touched.campaign_name && errors.campaign_name} />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Brand / client</label>
                    <Input topMargin="mt-0" inputPaddingClass="px-3" name="brand" placeholder="e.g. Acme GmbH" value={values.brand} onChange={handleChange} onBlur={handleBlur} error={touched.brand && errors.brand} />
                  </div>
                  <div>
                    <label className={labelClass}>Start date</label>
                    <input type="date" name="start_date" value={values.start_date} onChange={handleChange} onBlur={handleBlur} className={dateInputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>End date</label>
                    <input type="date" name="end_date" min={values.start_date || undefined} value={values.end_date} onChange={handleChange} onBlur={handleBlur} className={dateInputClass} />
                    {touched.end_date && errors.end_date && <p className="text-red-500 text-[10px] mt-1 px-2">{errors.end_date}</p>}
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Channels</label>
                    <div className="flex flex-wrap gap-2">
                      {CHANNELS.map(channel => {
                        const selected = values.providers.includes(channel)
                        return (
                          <button
                            key={channel}
                            type="button"
                            onClick={() => setFieldValue('providers', selected ? values.providers.filter(provider => provider !== channel) : [...values.providers, channel])}
                            className={`flex items-center gap-2 rounded-lg border-1 px-3 py-1.5 text-sm ${selected ? 'border-rb-green bg-rb-light-green dark:bg-rb-green/10 text-rb-black dark:text-white' : 'border-rb-lite-grey dark:border-rb-dark-mode-lighter text-rb-mat-grey dark:text-gray-400 hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter'}`}
                          >
                            {providerLogo(channel)}
                            {formatProvider(channel)}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Payout to creator</label>
                    <Input topMargin="mt-0" inputPaddingClass="px-3" name="payout_amount" placeholder="0.00" value={values.payout_amount} onChange={handleChange} onBlur={handleBlur} error={touched.payout_amount && errors.payout_amount} />
                  </div>
                  <div>
                    <label className={labelClass}>Currency</label>
                    <CurrencySelect name="currency" value={values.currency} onChange={currency => setFieldValue('currency', currency?.value || 'EUR')} error={touched.currency && errors.currency} noLabelPadding />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Notes</label>
                    <Textarea topMargin="mt-0" name="notes" rows={3} placeholder="Optional" value={values.notes} onChange={handleChange} onBlur={handleBlur} className="rounded-lg px-3 py-2 w-full border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter" />
                  </div>
                </div>
                <p className="text-xs text-rb-lighter-grey dark:text-gray-400">Internal only. Payouts are never shown to clients.</p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="w-full flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {isEdit && !confirmingDelete && (
                    <button type="button" onClick={() => setConfirmingDelete(true)} className="px-4 py-2 text-sm font-semibold rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10">
                      Delete
                    </button>
                  )}
                  {isEdit && confirmingDelete && (
                    <>
                      <span className="text-sm text-rb-black dark:text-white">Delete this collaboration?</span>
                      <button type="button" disabled={deleting} onClick={destroy} className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-red-600 disabled:opacity-50">
                        {deleting ? 'Deleting...' : 'Yes, delete'}
                      </button>
                      <button type="button" onClick={() => setConfirmingDelete(false)} className="rb-btn-cancel px-3 py-1.5 text-sm font-semibold rounded-lg">
                        No
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Modal.DissmissButton className="rb-btn-cancel px-4 py-2 text-sm font-semibold rounded-lg">
                    Cancel
                  </Modal.DissmissButton>
                  <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode disabled:opacity-50">
                    {isSubmitting ? 'Saving...' : (isEdit ? 'Save' : 'Add collaboration')}
                  </button>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
